import { ArrowDownOutlined, ArrowUpOutlined, ExclamationCircleOutlined, HolderOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Divider, Dropdown, Input, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { type DragEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import DownSelected from 'assets/svg/DownSelected';
import Down from 'assets/svg/Down';
import {
    formatPpaMetricValue,
    normalizePpaAttributeType,
    normalizePpaKpiIdsByLabel,
    normalizePpaKpiRows,
    uniquePpaKpisByLabel,
} from 'utils/ppaHelpers';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import {
    DimensionDropdownPanel,
    HeaderTitle,
    KpiDropdownPanel,
    StyledInfiniteTableWrapper,
    StyledSectionTabs,
    dimensionSelectionLimit,
    filterButtonClassName,
    filterButtonIconClassName,
    recordSummaryClassName,
    selectedRowClassName,
} from './performanceSummaryShared';
import {
    PERFORMANCE_SUMMARY_INFINITE_SCROLL_HEIGHT,
    type PerformanceSummaryDrilldownFilter,
    type PerformanceSummaryOrderByItem,
} from './hooks/usePerformanceSummaryKpis';
import usePerformanceSummaryKpisWithDelta from './hooks/usePerformanceSummaryKpisWithDelta';
import {
    ppaPerfSummarySectionTabTestId,
    ppaPerfSummaryDimensionBtnTestId,
    ppaPerfSummaryDimensionOptionTestId,
    ppaPerfSummaryDimensionSearchTestId,
    ppaPerfSummaryKpiApplyBtnTestId,
    ppaPerfSummaryKpiBtnTestId,
    ppaPerfSummaryKpiOptionTestId,
    ppaPerfSummaryKpiResetBtnTestId,
    ppaPerfSummaryKpiSelectAllTestId,
    ppaPerfSummarySortAscTestId,
    ppaPerfSummarySortDescTestId,
} from 'constants/ppaTestIds';

type TrendMetric = { value: string; raw_value: string; delta: string; direction: 'up' | 'down' };
type MetaAttr = { id: string; label: string, dimension?: string; is_attribute?: boolean; is_default?: boolean };
type MetaKpi = {
    id: string;
    label: string;
    is_default: boolean;
    is_mandatory?: boolean;
    mandatory?: boolean;
    isMandatory?: boolean;
    is_required?: boolean;
};

/** Single order_by entry per request; matches dimension column (Overall uses label in API aggregates). */
function overallAttributeSortActive(
    orderBy: PerformanceSummaryOrderByItem[] | undefined,
    dimensionConfig: MetaAttr,
): 'asc' | 'desc' | null {
    const o = orderBy?.[0];
    if (o?.id_type === 'attribute_type' && o.id === dimensionConfig.label) return o.direction;
    return null;
}

function kpiSortActiveFromOrderBy(
    orderBy: PerformanceSummaryOrderByItem[] | undefined,
    kpiId: string,
): 'asc' | 'desc' | null {
    const o = orderBy?.[0];
    if (o?.id_type === 'kpi_id' && o.id === kpiId) return o.direction;
    return null;
}
type RawMetaOption = {
    id: string;
    label: string;
    dimension?: string;
    is_attribute?: boolean;
    is_default?: boolean;
};
type RawMetaTab = {
    id: string;
    label: string;
    is_default?: boolean;
    group_by_options?: RawMetaOption[];
    column_options?: RawMetaOption[];
};
type MetaTab = {
    dimension: string;
    label: string;
    is_default: boolean;
    attribute_types: MetaAttr[];
    kpis: MetaKpi[];
    default_kpi_id_list?: string[];
};
type DropdownItem = { key: string; label: string; is_default?: boolean };
type KpiItem = {
    kpi_id: string;
    value?: string | number;
    raw_value?: string | number | null;
    valuePrefix?: string;
    valueSuffix?: string;
    growth_percentage?: number | null;
    trend_direction?: 'up' | 'down' | 'neutral';
};
type KpiNode = {
    dimension?: string;
    attribute_type?: string;
    ex_id?: string;
    description?: string;
    is_total?: boolean;
    kpis?: KpiItem[];
    data?: KpiNode[];
};
type Row = { key: string; dimensionValues: Record<string, string>; isTotal?: boolean; metrics: Record<string, TrendMetric> };
type TabSelection = { dimensions: string[]; kpis: string[] };
type OverallPerformanceMetaFeature = {
    feature_id: string;
    options?: {
        tabs?: RawMetaTab[];
    };
};
type OverallPerformanceSectionProps = {
    metaFeature?: OverallPerformanceMetaFeature;
    onDrilldownFiltersChange: (filters: PerformanceSummaryDrilldownFilter[]) => void;
};
export type OverallPerformanceExportHandle = {
    getExportData: () => { headers: string[]; rows: string[][] };
};
type SelectedDrilldownRow = {
    key: string;
    filters: PerformanceSummaryDrilldownFilter[];
};

const DEFAULT_METRIC: TrendMetric = { value: '-', raw_value: '-', delta: '-', direction: 'up' };

const withMetricAffixes = (value: string, prefix?: string, suffix?: string) => {
    if (value === '-') return value;

    let next = value;
    const normalizedPrefix = prefix?.trim();
    const normalizedSuffix = suffix?.trim();

    if (normalizedPrefix && !next.startsWith(normalizedPrefix)) next = `${normalizedPrefix}${next}`;
    if (normalizedSuffix && !next.endsWith(normalizedSuffix)) next = `${next}${normalizedSuffix}`;

    return next;
};

const toRawMetricValue = (rawValue?: string | number | null, fallbackValue?: string | number) => {
    const valueToUse = rawValue ?? fallbackValue;
    if (valueToUse == null) return '-';
    const normalized = String(valueToUse).trim();
    return normalized || '-';
};

const formatRawMetricForExport = (rawValue: string) => {
    if (rawValue === '-') return rawValue;
    const parsedRawValue = Number.parseFloat(rawValue.replace(/,/g, ''));
    return Number.isFinite(parsedRawValue) ? parsedRawValue.toFixed(2) : rawValue;
};

const metricFromKpi = (kpi?: KpiItem): TrendMetric =>
    !kpi
        ? DEFAULT_METRIC
        : {
            value: withMetricAffixes(formatPpaMetricValue(kpi.value), kpi.valuePrefix, kpi.valueSuffix),
            raw_value: toRawMetricValue(kpi.raw_value, kpi.value),
            delta: kpi.growth_percentage == null ? '-' : `${kpi.growth_percentage}%`,
            direction: kpi.trend_direction === 'down' ? 'down' : 'up',
        };

const toRows = (nodes: KpiNode[], selectedIds: string[], selectedDimensions: MetaAttr[]): Row[] => {
    const dimensionAttrTypeById = new Map(
        selectedDimensions.map((dimension) => [dimension.id, normalizePpaAttributeType(dimension.label)])
    );
    const rows: Row[] = [];

    const walk = (treeNodes: KpiNode[], inheritedValuesByAttrType: Record<string, string>) => {
        treeNodes.forEach((node, index) => {
            const attrTypeKey = normalizePpaAttributeType(node.attribute_type);
            const currentValue = String(node.description ?? node.ex_id ?? '-');
            const valuesByAttrType = { ...inheritedValuesByAttrType };

            if (attrTypeKey) valuesByAttrType[attrTypeKey] = currentValue;

            if (node.kpis?.length) {
                const map = new Map((node.kpis ?? []).map((kpi) => [kpi.kpi_id, kpi]));
                const metrics = selectedIds.reduce<Record<string, TrendMetric>>((acc, id) => {
                    acc[id] = metricFromKpi(map.get(id));
                    return acc;
                }, {});
                const dimensionValues = selectedDimensions.reduce<Record<string, string>>((acc, dimension) => {
                    const key = dimensionAttrTypeById.get(dimension.id) ?? '';
                    acc[dimension.id] = (key && valuesByAttrType[key]) || '-';
                    return acc;
                }, {});

                rows.push({
                    key: `${node.ex_id ?? node.description ?? 'row'}-${rows.length}-${index}`,
                    dimensionValues,
                    isTotal: Boolean(node.is_total),
                    metrics,
                });
            }

            if (node.data?.length) walk(node.data, valuesByAttrType);
        });
    };

    walk(nodes, {});
    return rows;
};

const TrendMetricCell = ({ metric, isTotal }: { metric: TrendMetric; isTotal?: boolean }) => {
    const isUp = metric.direction === 'up';
    return (
        <div className="flex items-center justify-start gap-2">
            <span style={{ textAlign: 'left' }} className={`text-base leading-[22px] text-black !min-w-[75px] ${isTotal ? 'font-semibold' : 'font-normal'}`}>{metric.value}</span>
            {metric.delta === '-' ? <span className={`text-base`}></span> :
                <div className='ml-2'>
                    <span className={`text-base mr-1 ${isUp ? 'text-[#14b8a6]' : 'text-[#ff4d4f]'}`}>
                        {isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    </span>
                    <span className={`text-base leading-[22px] text-black/65 ${isTotal ? 'font-semibold' : 'font-normal'}`}>{metric.delta}</span>
                </div>}
        </div>
    );
};

type PerformanceSectionHeaderProps = {
    title: string;
    activeDimensions: string[];
    dimensionMenuItems: DropdownItem[];
    defaultDimensionId?: string;
    onDimensionSelect: (keys: string[]) => void;
    selectedKpis: string[];
    kpiMenuItems: DropdownItem[];
    defaultKpis: string[];
    mandatoryKpis: string[];
    onKpiSelect: (keys: string[]) => void;
};

const PerformanceSectionHeader = ({
    title,
    activeDimensions,
    dimensionMenuItems,
    defaultDimensionId,
    onDimensionSelect,
    selectedKpis,
    kpiMenuItems,
    defaultKpis,
    mandatoryKpis,
    onKpiSelect,
}: PerformanceSectionHeaderProps) => {
    const [isDimensionDropdownOpen, setIsDimensionDropdownOpen] = useState(false);
    const [dimensionSearch, setDimensionSearch] = useState('');
    const [isKpiDropdownOpen, setIsKpiDropdownOpen] = useState(false);
    const [draftKpiIds, setDraftKpiIds] = useState<string[]>(selectedKpis);
    const [draftKpiOrder, setDraftKpiOrder] = useState<string[]>(kpiMenuItems.map((kpi) => kpi.key));
    const [draggingKpiId, setDraggingKpiId] = useState<string | null>(null);
    const [draggingOverKpiId, setDraggingOverKpiId] = useState<string | null>(null);

    const normalizedSearch = dimensionSearch.trim().toLowerCase();
    const filteredDimensionItems = normalizedSearch
        ? dimensionMenuItems.filter((item) => item.label.toLowerCase().includes(normalizedSearch))
        : dimensionMenuItems;
    const selectedDimensionCount = activeDimensions.length;
    const isDimensionSelectionLimitReached = selectedDimensionCount >= dimensionSelectionLimit;
    const kpiMenuItemById = useMemo(() => new Map(kpiMenuItems.map((item) => [item.key, item])), [kpiMenuItems]);
    const orderedKpiItems = useMemo(() => {
        const seen = new Set<string>();
        const ordered = draftKpiOrder
            .map((kpiId) => {
                seen.add(kpiId);
                return kpiMenuItemById.get(kpiId);
            })
            .filter((kpi): kpi is DropdownItem => Boolean(kpi));
        const missing = kpiMenuItems.filter((item) => !seen.has(item.key));
        return [...ordered, ...missing];
    }, [draftKpiOrder, kpiMenuItemById, kpiMenuItems]);
    const allKpisSelected = orderedKpiItems.length > 0 && draftKpiIds.length === orderedKpiItems.length;
    const mandatoryKpiSet = useMemo(() => new Set(mandatoryKpis), [mandatoryKpis]);

    useEffect(() => {
        if (!isKpiDropdownOpen) return;
        const kpiOrder = kpiMenuItems.map((kpi) => kpi.key);
        const validKpiIds = new Set(kpiOrder);
        setDraftKpiOrder(kpiOrder);
        setDraftKpiIds(selectedKpis.filter((kpiId) => validKpiIds.has(kpiId)));
    }, [isKpiDropdownOpen, kpiMenuItems, selectedKpis]);

    const onDimensionCheckboxChange = (dimensionId: string, checked: boolean) => {
        const isDefaultDimension = Boolean(defaultDimensionId) && dimensionId === defaultDimensionId;
        if (isDefaultDimension) return;

        const nextDimensionIds = checked
            ? [...activeDimensions, dimensionId]
            : activeDimensions.filter((key) => key !== dimensionId);
        onDimensionSelect(nextDimensionIds);
    };
    const onKpiCheckboxChange = (kpiId: string, checked: boolean) => {
        setDraftKpiIds((prev) => {
            if (!checked && mandatoryKpiSet.has(kpiId)) return prev;
            if (checked) return Array.from(new Set([...prev, kpiId]));
            const nextIds = prev.filter((id) => id !== kpiId);
            return nextIds.length ? nextIds : prev;
        });
    };
    const onKpiSelectAllToggle = () => {
        if (allKpisSelected) {
            const fallbackId = draftKpiOrder[0] ?? kpiMenuItems[0]?.key;
            if (fallbackId) setDraftKpiIds([fallbackId]);
            return;
        }
        setDraftKpiIds(orderedKpiItems.map((kpi) => kpi.key));
    };
    const onKpiDragStart = (event: DragEvent<HTMLDivElement>, kpiId: string) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', kpiId);
        setDraggingKpiId(kpiId);
        setDraggingOverKpiId(kpiId);
    };
    const onKpiDragOver = (event: DragEvent<HTMLDivElement>, kpiId: string) => {
        event.preventDefault();
        if (draggingOverKpiId !== kpiId) setDraggingOverKpiId(kpiId);
    };
    const onKpiDrop = (event: DragEvent<HTMLDivElement>, targetKpiId: string) => {
        event.preventDefault();
        const draggedKpiId = event.dataTransfer.getData('text/plain') || draggingKpiId;
        if (!draggedKpiId || draggedKpiId === targetKpiId) {
            setDraggingKpiId(null);
            setDraggingOverKpiId(null);
            return;
        }
        setDraftKpiOrder((prev) => {
            const currentOrder = prev.length ? prev : kpiMenuItems.map((kpi) => kpi.key);
            const from = currentOrder.indexOf(draggedKpiId);
            const to = currentOrder.indexOf(targetKpiId);
            if (from < 0 || to < 0) return currentOrder;
            const reordered = [...currentOrder];
            const [moved] = reordered.splice(from, 1);
            reordered.splice(to, 0, moved);
            return reordered;
        });
        setDraggingKpiId(null);
        setDraggingOverKpiId(null);
    };
    const onKpiDragEnd = () => {
        setDraggingKpiId(null);
        setDraggingOverKpiId(null);
    };
    const onKpiReset = () => {
        const nextOrder = kpiMenuItems.map((kpi) => kpi.key);
        const defaultKpiSet = new Set(defaultKpis);
        const defaultSelection = nextOrder.filter((kpiId) => defaultKpiSet.has(kpiId));
        const fallbackId = nextOrder[0];
        setDraftKpiOrder(nextOrder);
        setDraftKpiIds(defaultSelection.length ? defaultSelection : fallbackId ? [fallbackId] : []);
    };
    const onKpiApply = () => {
        const selectedSet = new Set(draftKpiIds);
        const orderedSelectedKpis = orderedKpiItems.map((kpi) => kpi.key).filter((kpiId) => selectedSet.has(kpiId));
        const fallbackId = orderedKpiItems[0]?.key;
        const nextSelection = orderedSelectedKpis.length ? orderedSelectedKpis : fallbackId ? [fallbackId] : [];
        if (nextSelection.length > 0) onKpiSelect(nextSelection);
        setIsKpiDropdownOpen(false);
    };

    return (
        <div className="flex items-center justify-between px-4 py-4">
            <h1 className="m-0 text-lg font-semibold leading-normal text-[#140B1D]">{title}</h1>
            <div className="flex items-center gap-3">
                <Dropdown
                    trigger={['click']}
                    open={isDimensionDropdownOpen}
                    onOpenChange={setIsDimensionDropdownOpen}
                    dropdownRender={() => (
                        <DimensionDropdownPanel className="w-[225px] rounded-lg border border-black/10 bg-white py-4 shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_6px_16px_0px_rgba(0,0,0,0.08),0px_9px_28px_8px_rgba(0,0,0,0.05)]">
                            <div className="px-4">
                                <Input
                                    value={dimensionSearch}
                                    onChange={(event) => setDimensionSearch(event.target.value)}
                                    placeholder="Search"
                                    suffix={<SearchOutlined className="text-black/45" />}
                                    className="h-8 !rounded-md"
                                    data-testid={ppaPerfSummaryDimensionSearchTestId('overall')}
                                />
                            </div>
                            <Divider className="!my-2" />
                            <div
                                className={`flex items-center gap-1 px-4 py-[6px] text-sm leading-4 ${
                                    isDimensionSelectionLimitReached ? 'text-attentionRed' : 'text-black/65'
                                }`}
                            >
                                <ExclamationCircleOutlined
                                    className={isDimensionSelectionLimitReached ? 'text-attentionRed' : 'text-black/65'}
                                />
                                <span>Select up to {dimensionSelectionLimit} dimensions</span>
                            </div>
                            <div className="max-h-[204px] overflow-y-auto px-4">
                                <div className="flex flex-col gap-1">
                                    {filteredDimensionItems.map((dimension) => {
                                        const isDefaultDimension = Boolean(defaultDimensionId) && dimension.key === defaultDimensionId;
                                        const isChecked = activeDimensions.includes(dimension.key);
                                        const isSelectionLimitReached =
                                            !isChecked && selectedDimensionCount >= dimensionSelectionLimit;
                                        const isDisabled = isDefaultDimension || isSelectionLimitReached;
                                        return (
                                            <Checkbox
                                                key={dimension.key}
                                                checked={isChecked}
                                                disabled={isDisabled}
                                                onChange={(event) =>
                                                    onDimensionCheckboxChange(dimension.key, event.target.checked)
                                                }
                                                className={`!text-sm !m-0 ${isDisabled ? 'opacity-50' : ''}`}
                                                data-testid={ppaPerfSummaryDimensionOptionTestId('overall', dimension.key)}
                                            >
                                                <div className='text-base text-black'>{dimension.label}</div>
                                            </Checkbox>
                                        );
                                    })}
                                </div>
                            </div>
                        </DimensionDropdownPanel>
                    )}
                >
                    <Button className={filterButtonClassName} data-testid={ppaPerfSummaryDimensionBtnTestId('overall')}>
                        <PlusOutlined className={filterButtonIconClassName} />
                        <div className="whitespace-nowrap font-medium">Dimension</div>
                        <div>
                            <Down />
                        </div>
                    </Button>
                </Dropdown>
                <Dropdown
                    trigger={['click']}
                    open={isKpiDropdownOpen}
                    onOpenChange={setIsKpiDropdownOpen}
                    dropdownRender={() => (
                        <KpiDropdownPanel className="w-[352px] rounded-lg border border-black/10 bg-[#fff] pt-5 shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)]">
                            <div className="px-6 pb-3 text-xs leading-normal text-[#9f9fa9]">
                                <div>Click to show or hide</div>
                                <div>Drag to change position</div>
                            </div>
                            <div className="px-4 pb-2">
                                <div className="mb-2 rounded border !border-borderGrey bg-white px-2 py-[5px]">
                                    <Checkbox
                                        checked={allKpisSelected}
                                        onChange={onKpiSelectAllToggle}
                                        className="!m-0 !text-sm !text-black/25"
                                        data-testid={ppaPerfSummaryKpiSelectAllTestId('overall')}
                                    >
                                        <div className="text-[13px] leading-[22px] text-black">Select All</div>
                                    </Checkbox>
                                </div>
                                <div className="max-h-[230px] overflow-y-auto pr-1">
                                    <div className="flex flex-col gap-2">
                                        {orderedKpiItems.map((kpi) => {
                                            const isChecked = draftKpiIds.includes(kpi.key);
                                            const isLastSelectedKpi = isChecked && draftKpiIds.length === 1;
                                            const isMandatoryKpi = mandatoryKpiSet.has(kpi.key);
                                            const isDropTarget = draggingOverKpiId === kpi.key && draggingKpiId !== kpi.key;
                                            return (
                                                <div
                                                    key={kpi.key}
                                                    draggable
                                                    onDragStart={(event) => onKpiDragStart(event, kpi.key)}
                                                    onDragOver={(event) => onKpiDragOver(event, kpi.key)}
                                                    onDrop={(event) => onKpiDrop(event, kpi.key)}
                                                    onDragEnd={onKpiDragEnd}
                                                    className={`flex items-center gap-2 rounded border !border-borderGrey bg-white px-2 py-[5px] ${isDropTarget ? 'border-[#44338a]' : 'border-black/6'} ${draggingKpiId === kpi.key ? 'opacity-70' : ''}`}
                                                >
                                                    <span className="inline-flex cursor-grab items-center text-[#44338a] active:cursor-grabbing">
                                                        <HolderOutlined />
                                                    </span>
                                                    <Checkbox
                                                        checked={isChecked}
                                                        disabled={isLastSelectedKpi || isMandatoryKpi}
                                                        onChange={(event) => onKpiCheckboxChange(kpi.key, event.target.checked)}
                                                        className="!m-0 !text-sm"
                                                        data-testid={ppaPerfSummaryKpiOptionTestId('overall', kpi.key)}
                                                    >
                                                        <div className="text-[13px] leading-[22px] text-black/88">{kpi.label}</div>
                                                    </Checkbox>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-2">
                                <Button
                                    onClick={onKpiReset}
                                    className="!h-8 !rounded-[8px] !border-[#d9d9d9] !bg-white !px-4 !text-[14px] !font-semibold !text-black hover:!border-[#d9d9d9] hover:!bg-white"
                                    data-testid={ppaPerfSummaryKpiResetBtnTestId('overall')}
                                >
                                    Reset
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={onKpiApply}
                                    className="!h-8 !rounded-[8px] !px-4 !text-[14px] !font-normal"
                                    data-testid={ppaPerfSummaryKpiApplyBtnTestId('overall')}
                                >
                                    Apply
                                </Button>
                            </div>
                        </KpiDropdownPanel>
                    )}
                >
                    <Button className={filterButtonClassName} data-testid={ppaPerfSummaryKpiBtnTestId('overall')}>
                        <PlusOutlined className={filterButtonIconClassName} />
                        <div className="whitespace-nowrap font-medium">KPIs</div>
                        <div>
                            <Down />
                        </div>
                    </Button>
                </Dropdown>
            </div>
        </div>
    );
};

const OverallPerformanceSection = forwardRef<OverallPerformanceExportHandle, OverallPerformanceSectionProps>(({
    metaFeature,
    onDrilldownFiltersChange,
}, ref) => {
    const dimensionTabs = useMemo<MetaTab[]>(() => {
        const tabs = metaFeature?.options?.tabs ?? [];
        return tabs.map((tab) => ({
            dimension: tab.id,
            label: tab.label,
            is_default: Boolean(tab.is_default),
            attribute_types: (tab.group_by_options ?? []).map((option) => ({
                id: option.id,
                label: option.label,
                dimension: option.dimension ?? tab.id,
                is_attribute: option.is_attribute ?? true,
                is_default: option.is_default,
            })),
            kpis: (tab.column_options ?? []).map((option) => ({
                id: option.id,
                label: option.label,
                is_default: Boolean(option.is_default),
                is_mandatory: Boolean((option as any).is_mandatory),
                mandatory: Boolean((option as any).mandatory),
                isMandatory: Boolean((option as any).isMandatory),
                is_required: Boolean((option as any).is_required),
            })),
            default_kpi_id_list: (tab.column_options ?? [])
                .filter((option) => option.is_default)
                .map((option) => option.id),
        }));
    }, [metaFeature]);

    const defaultTab = dimensionTabs.find((tab) => tab.is_default)?.dimension ?? dimensionTabs[0]?.dimension ?? '';

    const [activeTab, setActiveTab] = useState('');
    const [tabSelections, setTabSelections] = useState<Record<string, TabSelection>>({});
    const [selectedDrilldownRows, setSelectedDrilldownRows] = useState<SelectedDrilldownRow[]>([]);
    const [orderBy, setOrderBy] = useState<PerformanceSummaryOrderByItem[] | undefined>(undefined);

    const activeTabConfig = useMemo(
        () => dimensionTabs.find((tab) => tab.dimension === activeTab) ?? dimensionTabs[0],
        [activeTab, dimensionTabs]
    );

    const activeSelection = tabSelections[activeTab] ?? { dimensions: [], kpis: [] };
    const activeDimensions = activeSelection.dimensions;
    const selectedKpis = activeSelection.kpis;

    const tabKpis = useMemo(() => uniquePpaKpisByLabel(activeTabConfig?.kpis ?? []), [activeTabConfig]);
    const mandatoryKpis = useMemo(
        () =>
            tabKpis
                .filter((kpi) => kpi.is_mandatory || kpi.mandatory || kpi.isMandatory || kpi.is_required)
                .map((kpi) => kpi.id),
        [tabKpis]
    );

    const selectedDimensionConfigs = useMemo(() => {
        const attributes = activeTabConfig?.attribute_types ?? [];
        const byId = new Map(attributes.map((attr) => [attr.id, attr]));
        return activeDimensions.map((id) => byId.get(id)).filter((attr): attr is MetaAttr => Boolean(attr));
    }, [activeDimensions, activeTabConfig]);

    const selectedAggregateAttributeTypes = useMemo(
        () => selectedDimensionConfigs.map((dimensionConfig) => dimensionConfig.label).filter(Boolean),
        [selectedDimensionConfigs]
    );
    const selectedAggregateDimensions = useMemo(
        () => selectedDimensionConfigs.map((dimensionConfig) => dimensionConfig.dimension ?? activeTabConfig?.dimension),
        [activeTabConfig?.dimension, selectedDimensionConfigs]
    );
    const selectedAggregateIsAttributes = useMemo(
        () => selectedDimensionConfigs.map((dimensionConfig) => dimensionConfig.is_attribute ?? true),
        [selectedDimensionConfigs]
    );

    useEffect(() => {
        if (!dimensionTabs.some((tab) => tab.dimension === activeTab)) setActiveTab(defaultTab);
    }, [activeTab, defaultTab, dimensionTabs]);

    const defaultKpis = useMemo(() => {
        if (!activeTabConfig) return [];
        const all = activeTabConfig.kpis ?? [];
        const mandatorySet = new Set(
            all
                .filter((kpi) => kpi.is_mandatory || kpi.mandatory || kpi.isMandatory || kpi.is_required)
                .map((kpi) => kpi.id)
        );
        const fromConfig = normalizePpaKpiIdsByLabel(activeTabConfig.default_kpi_id_list?.filter(Boolean) ?? [], all);
        if (fromConfig.length) {
            return Array.from(new Set([...Array.from(mandatorySet), ...fromConfig]));
        }
        const defaults = normalizePpaKpiIdsByLabel(all.filter((kpi) => kpi.is_default).map((kpi) => kpi.id), all);
        if (defaults.length) return Array.from(new Set([...Array.from(mandatorySet), ...defaults]));
        return tabKpis[0]?.id ? [tabKpis[0].id] : [];
    }, [activeTabConfig, tabKpis]);

    /**
     * Anchor KPI for the backend: it drives pagination/ordering and must stay
     * stable across KPI add/remove for the per-KPI delta cache to remain valid.
     * Sort-by-KPI swaps the anchor (backend pagination is anchor-driven, so the
     * sort column must be the anchor for correct row order).
     */
    const anchorKpiId = useMemo(() => {
        const sortKpi = orderBy?.[0]?.id_type === 'kpi_id' ? orderBy[0].id : undefined;
        if (sortKpi && selectedKpis.includes(sortKpi)) return sortKpi;
        const fromMandatory = mandatoryKpis.find((id) => selectedKpis.includes(id));
        if (fromMandatory) return fromMandatory;
        const fromDefault = defaultKpis.find((id) => selectedKpis.includes(id));
        if (fromDefault) return fromDefault;
        return selectedKpis[0] ?? '';
    }, [orderBy, selectedKpis, mandatoryKpis, defaultKpis]);

    useEffect(() => {
        if (!activeTabConfig || !activeTab) return;
        setTabSelections((prev) => {
            const currentSelection = prev[activeTab] ?? { dimensions: [], kpis: [] };
            const defaultDimension = activeTabConfig.attribute_types[0]?.id ?? '';
            const validDimensionIds = new Set(activeTabConfig.attribute_types.map((a) => a.id));
            const filteredDimensions = currentSelection.dimensions.filter((id) => validDimensionIds.has(id));
            const normalizedDimensions = defaultDimension
                ? [defaultDimension, ...filteredDimensions.filter((id) => id !== defaultDimension)]
                : filteredDimensions;
            const nextDimensions = normalizedDimensions.slice(0, dimensionSelectionLimit);

            const validKpiIds = new Set(tabKpis.map((kpi) => kpi.id));
            const filteredKpis = normalizePpaKpiIdsByLabel(
                currentSelection.kpis.filter((id) => validKpiIds.has(id)),
                activeTabConfig.kpis ?? []
            );
            const nextKpis = filteredKpis.length ? filteredKpis : defaultKpis;

            const dimensionsUnchanged =
                nextDimensions.length === currentSelection.dimensions.length &&
                nextDimensions.every((id, index) => id === currentSelection.dimensions[index]);
            const kpisUnchanged =
                nextKpis.length === currentSelection.kpis.length &&
                nextKpis.every((id, index) => id === currentSelection.kpis[index]);

            if (dimensionsUnchanged && kpisUnchanged) return prev;
            return {
                ...prev,
                [activeTab]: {
                    dimensions: nextDimensions,
                    kpis: nextKpis,
                },
            };
        });
    }, [activeTab, activeTabConfig, defaultKpis, tabKpis]);

    const dimensionMenuItems: DropdownItem[] = (activeTabConfig?.attribute_types ?? []).map((dimension) => ({
        key: dimension.id,
        label: dimension.label,
        is_default: dimension.is_default,
    }));
    const kpiMenuItems: DropdownItem[] = tabKpis.map((kpi) => ({
        key: kpi.id,
        label: kpi.label,
    }));

    const {
        data: kpiInfiniteData,
        isLoading: isKpisLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        pendingKpiIds,
    } = usePerformanceSummaryKpisWithDelta<KpiNode>({
        scope: 'performance-summary-kpis',
        kpiIds: selectedKpis,
        anchorKpiId,
        dimension: activeTabConfig?.dimension,
        aggregateAttributeTypes: selectedAggregateAttributeTypes,
        aggregateDimensions: selectedAggregateDimensions,
        aggregateIsAttributes: selectedAggregateIsAttributes,
        orderBy,
        normalizeData: (rows) =>
            normalizePpaKpiRows<KpiItem>(rows, (kpi) => ({
                kpi_id: kpi.kpi_id,
                value: kpi.value,
                raw_value: kpi.raw_value,
                valuePrefix: kpi.valuePrefix,
                valueSuffix: kpi.valueSuffix,
                growth_percentage: kpi.growth_percentage ?? null,
                trend_direction: kpi.trend_direction ?? 'neutral',
            })) as KpiNode[],
        enabled: Boolean(
            activeTabConfig?.dimension &&
                selectedAggregateAttributeTypes.length &&
                selectedKpis.length > 0 &&
                anchorKpiId
        ),
    });

    const sourceRows = useMemo(
        () => kpiInfiniteData?.pages.flatMap((page) => page.nodes) ?? [],
        [kpiInfiniteData?.pages],
    );

    const totalRecordCount = kpiInfiniteData?.pages?.[0]?.total;

    const overallPerformanceRows = useMemo(
        () => toRows(sourceRows, selectedKpis, selectedDimensionConfigs),
        [selectedDimensionConfigs, selectedKpis, sourceRows]
    );

    const isInitialLoading = isKpisLoading && overallPerformanceRows.length === 0;
    const isLoadingMore = isFetchingNextPage;
    const pendingKpiIdSet = useMemo(() => new Set(pendingKpiIds), [pendingKpiIds]);

    const overallPerformanceColumns = useMemo<ColumnsType<Row>>(() => {
        const setOverallAttributeSort = (dimensionConfig: MetaAttr, direction: 'asc' | 'desc') => {
            setOrderBy([{ id_type: 'attribute_type', id: dimensionConfig.label, direction }]);
        };

        const setOverallKpiSort = (kpiId: string, direction: 'asc' | 'desc') => {
            setOrderBy([{ id_type: 'kpi_id', id: kpiId, direction }]);
        };

        const rowDimensionColumns: ColumnsType<Row> = selectedDimensionConfigs.map((dimensionConfig) => {
            const sortActive = overallAttributeSortActive(orderBy, dimensionConfig);
            return {
                title: (
                    <HeaderTitle
                        title={dimensionConfig.label}
                        sortActive={sortActive}
                        onSortAsc={() => setOverallAttributeSort(dimensionConfig, 'asc')}
                        onSortDesc={() => setOverallAttributeSort(dimensionConfig, 'desc')}
                        sortAscTestId={ppaPerfSummarySortAscTestId('overall', dimensionConfig.id)}
                        sortDescTestId={ppaPerfSummarySortDescTestId('overall', dimensionConfig.id)}
                    />
                ),
                key: `dimension-${dimensionConfig.id}`,
                width: 220,
                fixed: 'left',
                render: (_: unknown, row: Row) => (
                    <span className={row.isTotal ? 'font-semibold text-black' : 'font-normal text-black/88'}>
                        {row.dimensionValues[dimensionConfig.id] ?? '-'}
                    </span>
                ),
            };
        });

        const kpiById = new Map(tabKpis.map((kpi) => [kpi.id, kpi]));
        const kpiColumns = selectedKpis
            .map((kpiId) => kpiById.get(kpiId))
            .filter((kpi): kpi is MetaKpi => Boolean(kpi))
            .map((kpi) => {
                const sortActive = kpiSortActiveFromOrderBy(orderBy, kpi.id);
                return {
                    title: (
                        <HeaderTitle
                            title={kpi.label}
                            withVs
                            pending={pendingKpiIdSet.has(kpi.id)}
                            sortActive={sortActive}
                            onSortAsc={() => setOverallKpiSort(kpi.id, 'asc')}
                            onSortDesc={() => setOverallKpiSort(kpi.id, 'desc')}
                            sortAscTestId={ppaPerfSummarySortAscTestId('overall', kpi.id)}
                            sortDescTestId={ppaPerfSummarySortDescTestId('overall', kpi.id)}
                        />
                    ),
                    dataIndex: kpi.id,
                    key: kpi.id,
                    align: 'center' as const,
                    width: 200,
                    render: (_metric: TrendMetric, row: Row) => (
                        <TrendMetricCell metric={row.metrics[kpi.id] ?? DEFAULT_METRIC} isTotal={row.isTotal} />
                    ),
                };
            });

        return [...rowDimensionColumns, ...kpiColumns];
    }, [orderBy, pendingKpiIdSet, selectedDimensionConfigs, selectedKpis, tabKpis]);

    const overallPerformanceTableScrollX = useMemo(
        () => overallPerformanceColumns.reduce((acc, col) => acc + (typeof col.width === 'number' ? col.width : 180), 0),
        [overallPerformanceColumns]
    );
    const handleLoadMore = () => {
        if (!hasNextPage || isLoadingMore) return;
        void fetchNextPage();
    };

    useImperativeHandle(
        ref,
        () => ({
            getExportData: () => {
                const selectedDimensions = selectedDimensionConfigs.map((dimensionConfig) => dimensionConfig.label);
                const selectedKpiLabels = selectedKpis.map((kpiId) => tabKpis.find((kpi) => kpi.id === kpiId)?.label ?? kpiId);
                const headers = [...selectedDimensions, ...selectedKpiLabels];
                const rows = overallPerformanceRows.map((row) => {
                    const dimensionCells = selectedDimensionConfigs.map((dimensionConfig) => row.dimensionValues[dimensionConfig.id] ?? '-');
                    const metricCells = selectedKpis.map((kpiId) => {
                        const metric = row.metrics[kpiId];
                        if (!metric) return '-';
                        const rawMetricValue = formatRawMetricForExport(metric.raw_value);
                        return metric.delta && metric.delta !== '-' ? `${rawMetricValue} (${metric.delta})` : rawMetricValue;
                    });
                    return [...dimensionCells, ...metricCells];
                });
                return { headers, rows };
            },
        }),
        [overallPerformanceRows, selectedDimensionConfigs, selectedKpis, tabKpis]
    );

    const handleDimensionSelect = (keys: string[]) => {
        if (!activeTabConfig || !activeTab) return;
        const validDimensionIds = new Set((activeTabConfig?.attribute_types ?? []).map((a) => a.id));
        const deduped = Array.from(new Set(keys)).filter((id) => validDimensionIds.has(id));
        const defaultDimension = activeTabConfig?.attribute_types[0]?.id ?? '';
        const normalizedDimensions = defaultDimension ? [defaultDimension, ...deduped.filter((id) => id !== defaultDimension)] : deduped;
        const nextDimensions = normalizedDimensions.slice(0, dimensionSelectionLimit);
        if (nextDimensions.length > 0) {
            setTabSelections((prev) => ({
                ...prev,
                [activeTab]: {
                    dimensions: nextDimensions,
                    kpis: prev[activeTab]?.kpis ?? selectedKpis,
                },
            }));
        }
        setSelectedDrilldownRows([]);
        onDrilldownFiltersChange([]);
        setOrderBy(undefined);
    };

    const handleKpiSelectionChange = (keys: string[]) => {
        if (!activeTabConfig || !activeTab || keys.length === 0) return;
        setTabSelections((prev) => ({
            ...prev,
            [activeTab]: {
                dimensions: prev[activeTab]?.dimensions ?? activeDimensions,
                kpis: keys,
            },
        }));
        onDrilldownFiltersChange([]);
        setOrderBy(undefined);
    };
    const handleTabChange = (tab: string) => {
        setActiveTab(tab);
        setSelectedDrilldownRows([]);
        onDrilldownFiltersChange([]);
        setOrderBy(undefined);
    };

    return (
        <div className="w-full min-w-0 border border-borderGrey rounded-lg">
            <PerformanceSectionHeader
                title="Overall Performance"
                activeDimensions={activeDimensions}
                dimensionMenuItems={dimensionMenuItems}
                defaultDimensionId={activeTabConfig?.attribute_types[0]?.id}
                onDimensionSelect={handleDimensionSelect}
                selectedKpis={selectedKpis}
                kpiMenuItems={kpiMenuItems}
                defaultKpis={defaultKpis}
                mandatoryKpis={mandatoryKpis}
                onKpiSelect={handleKpiSelectionChange}
            />
            <StyledSectionTabs
                type="card"
                size="small"
                activeKey={activeTab}
                onChange={handleTabChange}
                items={dimensionTabs.map((tab) => ({
                    key: tab.dimension,
                    label: (
                        <span data-testid={ppaPerfSummarySectionTabTestId('overall', tab.dimension)}>
                            {tab.label}
                        </span>
                    ),
                }))}
            />
            <StyledInfiniteTableWrapper>
                <InfiniteScroller
                    data={overallPerformanceRows}
                    loading={isInitialLoading || isLoadingMore}
                    hasMore={Boolean(hasNextPage)}
                    height={PERFORMANCE_SUMMARY_INFINITE_SCROLL_HEIGHT}
                    onNext={handleLoadMore}
                >
                    <Table
                        id="occasionTable"
                        sticky
                        pagination={false}
                        columns={overallPerformanceColumns}
                        dataSource={overallPerformanceRows}
                        bordered={false}
                        loading={isInitialLoading}
                        scroll={{ x: overallPerformanceTableScrollX, y: PERFORMANCE_SUMMARY_INFINITE_SCROLL_HEIGHT }}
                        style={{
                            opacity: isFetchingNextPage ? 0.7 : 1,
                            transition: 'opacity 0.2s',
                        }}
                        locale={{
                            emptyText: (
                                <div
                                    className="flex font-md text-borderGrey  justify-center items-center"
                                    style={{ height: 400 }}
                                >
                                    No data available
                                </div>
                            ),
                        }}
                        rowClassName={(row: Row) =>
                            `${selectedDrilldownRows.some((selectedRow) => selectedRow.key === row.key) ? selectedRowClassName : ''} cursor-pointer`.trim()
                        }
                        onRow={(record) => ({
                            onClick: () => {
                                const nextFilters = selectedDimensionConfigs.reduce<PerformanceSummaryDrilldownFilter[]>((acc, dimensionConfig) => {
                                    const dimensionValue = record.dimensionValues[dimensionConfig.id];
                                    const filterDimension = dimensionConfig.dimension ?? activeTabConfig?.dimension;
                                    if (!filterDimension || !dimensionConfig.label || !dimensionValue || dimensionValue === '-') return acc;
                                    acc.push({
                                        dimension: filterDimension,
                                        attribute_type: dimensionConfig.label,
                                        dim_attribute_ex_id: dimensionValue,
                                        dim_attribute_ex_id_desc: dimensionValue,
                                        is_attribute: dimensionConfig.is_attribute ?? true,
                                    });
                                    return acc;
                                }, []);
                                if (nextFilters.length === 0 || record.isTotal) {
                                    setSelectedDrilldownRows([]);
                                    onDrilldownFiltersChange([]);
                                    return;
                                }
                                setSelectedDrilldownRows((prev) => {
                                    const alreadySelected = prev.some((selectedRow) => selectedRow.key === record.key);
                                    const nextSelectedRows = alreadySelected
                                        ? prev.filter((selectedRow) => selectedRow.key !== record.key)
                                        : [...prev, { key: record.key, filters: nextFilters }];
                                    const dedupedFilters = Array.from(
                                        new Map(
                                            nextSelectedRows
                                                .flatMap((selectedRow) => selectedRow.filters)
                                                .map((filter) => [
                                                    `${filter.dimension}::${filter.attribute_type}::${filter.dim_attribute_ex_id}`,
                                                    filter,
                                                ])
                                        ).values()
                                    );
                                    onDrilldownFiltersChange(dedupedFilters);
                                    return nextSelectedRows;
                                });
                            },
                        })}
                    />
                </InfiniteScroller>
            </StyledInfiniteTableWrapper>
            <div className="rounded-[3px]">
                <div className={recordSummaryClassName}>
                    Showing {overallPerformanceRows.length}
                    {totalRecordCount !== undefined ? ` of ${totalRecordCount}` : ''} records
                </div>
            </div>
        </div>
    );
});

export default OverallPerformanceSection;
